// src/components/common/NotificationBell.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../api';

const playPing = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return false;
    const ctx = window.__erpNotificationAudioCtx || new AudioCtx();
    window.__erpNotificationAudioCtx = ctx;
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.3);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
    return true;
  } catch (e) {
    return false;
  }
};

function NotificationBell({ user, currentTab, onNavigate, onCounts }) {
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [error, setError] = useState("");
  const prevTotal = useRef(null);

  const unlockAudio = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = window.__erpNotificationAudioCtx || new AudioCtx();
      window.__erpNotificationAudioCtx = ctx;
      ctx.resume?.();
      setAudioReady(true);
    } catch (e) {}
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await api.getNotifications();
      const newTotal = data.total || 0;
      if (prevTotal.current !== null && newTotal > prevTotal.current) {
        playPing();
      }
      prevTotal.current = newTotal;
      setCounts(data);
      setError("");
      if (onCounts) onCounts(data);
    } catch (e) {
      setError(e.message || "Notifications could not be loaded.");
      if (onCounts) onCounts({});
    }
  }, [onCounts]);

  const clearAmbassadorNotifications = useCallback(async () => {
    if (user.role === "ambassador") {
      try {
        await api.clearAmbassadorNotifications();
        await load();
      } catch (e) {
        console.error("Failed to clear notifications:", e);
      }
    }
  }, [user.role, load]);

  const handleBellClick = async () => {
    unlockAudio();
    const willBeOpen = !open;
    setOpen(willBeOpen);
    if (willBeOpen && user.role === "ambassador") {
      await clearAmbassadorNotifications();
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, [unlockAudio]);

  useEffect(() => { load(); }, [currentTab]);

  const total = counts.total || 0;

  const items = [];
  if (user.role === "manager") {
    if (counts.expenses > 0) items.push({ key: "expenses", label: `${counts.expenses} expense${counts.expenses !== 1 ? "s" : ""} pending approval`, icon: "🧾" });
    if (counts.inventory > 0) items.push({ key: "inventory", label: `${counts.inventory} restock${counts.inventory !== 1 ? "s" : ""} pending approval`, icon: "📦" });
    if (counts.sales > 0) items.push({ key: "pendingsales", label: `${counts.sales} sale${counts.sales !== 1 ? "s" : ""} awaiting confirmation`, icon: "🛒" });
    if (counts.withdrawals > 0) items.push({ key: "ambassadors", label: `${counts.withdrawals} withdrawal${counts.withdrawals !== 1 ? "s" : ""} to process`, icon: "💸" });
  } else if (user.role === "cashier") {
    if (counts.sales > 0) items.push({ key: "pendingsales", label: `${counts.sales} sale${counts.sales !== 1 ? "s" : ""} awaiting payment confirmation`, icon: "🛒" });
  } else if (user.role === "ambassador") {
    if (counts.newActivity > 0) items.push({ key: "ambdash", label: `${counts.newActivity} new commission${counts.newActivity !== 1 ? "s" : ""} in last 24h`, icon: "🏅" });
  }

  return (
    <div className="notification-wrap">
      <button
        className={`notification-bell${total > 0 ? " has-items" : ""}`}
        onClick={handleBellClick}
        title={total > 0 ? `${total} item${total !== 1 ? "s" : ""} need your attention` : "All clear"}
        aria-label={total > 0 ? `${total} notifications` : "No notifications"}
      >
        <span className="notification-icon">🔔</span>
        {total > 0 && <span className="notification-pulse" />}
        {total > 0 && (
          <span className="notification-dot">{total > 99 ? "99+" : total}</span>
        )}
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 998 }} />
          <div className="notification-menu">
            <div className="notification-menu-header">
              <div>
                <div className="notification-title">Action center</div>
                <div className="notification-subtitle">
                  {total > 0 ? `${total} item${total !== 1 ? "s" : ""} waiting for you` : "Everything is up to date"}
                </div>
              </div>
              <button className="notification-refresh" onClick={() => load()}>Refresh</button>
            </div>
            {items.length === 0 ? (
              <div className="notification-empty">
                <div className="notification-empty-icon">✓</div>
                All clear. Nothing needs attention right now.
              </div>
            ) : (
              items.map(item => (
                <button key={item.key} onClick={() => { onNavigate(item.key); setOpen(false); }} className="notification-item">
                  <span className="notification-item-icon">{item.icon}</span>
                  <span>
                    <span className="notification-item-label">{item.label}</span>
                    <span className="notification-item-meta">Open this workspace</span>
                  </span>
                  <span className="notification-arrow">›</span>
                </button>
              ))
            )}
            <div className="notification-audio">
              Sound {audioReady ? "ready" : "starts after your first click or key press"}.
            </div>
            {error && (
              <div className="notification-error">
                Notifications are not reading right now: {error}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationBell;